import React, { memo } from 'react';

import orderBy from 'lodash.orderby';
import type { FieldValues } from 'react-hook-form';

import type { QuickAddSectionProps, QuickItemRenderer } from '@/_containers';
import { AsyncQuickAddSection, phoneNumberIncludeKey } from '@/_containers';
import { nameResolver } from '@/_shared';

import { ItemWithPhoneNumber } from './item-with-phone-number';

import { useGetContactsMutation } from '../../apis';
import type { ContactLinkerWithPhoneNumberPivotQuerySchema } from '../../schemas';

type ContactMultiSelectProps<TFormValues extends FieldValues = FieldValues> = Pick<
  QuickAddSectionProps<ContactLinkerWithPhoneNumberPivotQuerySchema, TFormValues>,
  'isEditable' | 'control' | 'fieldName' | 'title' | 'sectionTitle' | 'icon' | 'rightSlot' | 'paginatedItems'
> & {
  contactId?: string | number;
  items: ContactLinkerWithPhoneNumberPivotQuerySchema[];
  showHeader?: boolean;
  showPrimaryContactAction?: boolean;
  hasPrimary?: boolean;
  primaryContactsMap?: Record<number, ContactLinkerWithPhoneNumberPivotQuerySchema>;
  setContactType?: (item: ContactLinkerWithPhoneNumberPivotQuerySchema, isPrimary: boolean) => void;
};

const apiParams = { include: phoneNumberIncludeKey() };

function AsyncContactMultiSelectWithPhoneNumberBase<TFormValues extends FieldValues = FieldValues>({
  contactId,
  title = 'Contacts',
  sectionTitle = 'Contacts',
  items = [],
  showPrimaryContactAction = false,
  hasPrimary = false,
  primaryContactsMap = {},
  setContactType,
  ...rest
}: ContactMultiSelectProps<TFormValues>) {
  const renderItem: QuickItemRenderer<ContactLinkerWithPhoneNumberPivotQuerySchema> = React.useCallback(
    (item, { isEditable, removeItem }) => (
      <ItemWithPhoneNumber
        key={item.id}
        item={item}
        isEditable={isEditable}
        removeItemHandler={removeItem}
        showPrimaryContactAction={showPrimaryContactAction}
        isPrimary={!!primaryContactsMap[item.id]}
        hasPrimary={hasPrimary}
        setContactType={setContactType}
      />
    ),
    [showPrimaryContactAction, primaryContactsMap, hasPrimary, setContactType]
  );

  const itemToString = React.useCallback(
    (item: ContactLinkerWithPhoneNumberPivotQuerySchema | null) =>
      item ? nameResolver(item.first_name, item.last_name) : '',
    []
  );

  const keyExtractor = React.useCallback(
    (item: ContactLinkerWithPhoneNumberPivotQuerySchema) => item.id.toString(),
    []
  );

  const sortValues = React.useCallback(
    // TO SEE THE DIFFERENCE, CHANGE 'name' TO 'itemToString'
    (values: ContactLinkerWithPhoneNumberPivotQuerySchema[]) => orderBy(values, itemToString),
    [itemToString]
  );

  const filterOptions = React.useCallback(
    (item: ContactLinkerWithPhoneNumberPivotQuerySchema) => (contactId ? item.id === Number(contactId) : false),
    [contactId]
  );

  const sortedItems = React.useMemo(() => sortValues(items), [items, sortValues]);

  return (
    <AsyncQuickAddSection
      {...rest}
      sectionTitle={sectionTitle}
      title={title}
      emptyDataMessage="No Contacts"
      items={sortedItems}
      keyExtractor={keyExtractor}
      itemToString={itemToString}
      renderItem={renderItem}
      useApi={useGetContactsMutation}
      sortValues={sortValues}
      filterOptions={filterOptions}
      apiParams={apiParams}
    />
  );
}

export const AsyncContactMultiSelectWithPhoneNumber = memo(
  AsyncContactMultiSelectWithPhoneNumberBase
) as typeof AsyncContactMultiSelectWithPhoneNumberBase;
